import type { LessonBundle, MediaRef, Word } from '@ezishizi/content-schema';
import { chestPrize, stickerChars } from './chest.ts';
import {
  nodeDone,
  type IndexLesson,
  type Island,
  type PathNode,
  type PathSection,
} from './path.ts';
import type { ProgressMap } from './progress.ts';

/**
 * The sticker book. A word's sticker is earned when the child finishes the island it is taught
 * on (every land stop done; the Challenge and the reward are not needed). A chest that held a
 * shiny sticker adds a gold copy of one of its island's words. All of it is derived from
 * progress; nothing is stored.
 */
export interface Sticker {
  /** Unique within the book: `<setId>:<char>` plus `:shiny` for the gold copy. */
  id: string;
  lessonId: string;
  setId: string;
  char: string;
  media: MediaRef;
  earned: boolean;
  shiny: boolean;
}

export interface StickerPage {
  lesson: IndexLesson;
  stickers: Sticker[];
  /** Stickers earned on this page, shiny ones included. */
  earned: number;
}

/** The stops a child walks on the island itself: everything but the gate and the reward. */
export function landNodes(island: Island): PathNode[] {
  return island.nodes.filter(
    (n) => n.kind !== 'challenge' && n.kind !== 'practice' && n.kind !== 'reward',
  );
}

export function islandFinished(island: Island, progress: ProgressMap): boolean {
  const land = landNodes(island);
  return land.length > 0 && land.every((n) => nodeDone(n, progress));
}

/** The island's chest reward node, when its reward is a bonus game (a story reward is no chest). */
export function chestNode(island: Island): PathNode | null {
  return island.nodes.find((n) => n.kind === 'reward' && n.reward.type === 'game') ?? null;
}

/** The char of the shiny sticker this island's opened chest gave, if it gave one. */
export function shinyChar(island: Island, progress: ProgressMap, profileId: string): string | null {
  const chest = chestNode(island);
  if (!chest || !nodeDone(chest, progress)) return null;
  const prize = chestPrize(profileId, island.set.id, stickerChars(island.set));
  return prize.type === 'sticker' ? prize.char : null;
}

export function buildStickerPages(
  sections: PathSection[],
  progress: ProgressMap,
  profileId: string,
): StickerPage[] {
  return sections.map((section) => {
    const stickers: Sticker[] = [];
    for (const island of section.islands) {
      const { set } = island;
      const done = islandFinished(island, progress);
      const byChar = new Map(set.pictures.map((p) => [p.char, p.media]));
      for (const char of stickerChars(set))
        stickers.push({
          id: `${set.id}:${char}`,
          lessonId: section.lesson.id,
          setId: set.id,
          char,
          media: byChar.get(char)!,
          earned: done,
          shiny: false,
        });
    }
    // Gold copies come last and only once won: an unopened chest stays a surprise.
    for (const island of section.islands) {
      const char = shinyChar(island, progress, profileId);
      const media = char ? island.set.pictures.find((p) => p.char === char)?.media : undefined;
      if (char && media)
        stickers.push({
          id: `${island.set.id}:${char}:shiny`,
          lessonId: section.lesson.id,
          setId: island.set.id,
          char,
          media,
          earned: true,
          shiny: true,
        });
    }
    return { lesson: section.lesson, stickers, earned: stickers.filter((s) => s.earned).length };
  });
}

/** The page to open the book on: the last lesson with something earned, else the first. */
export function defaultStickerPage(pages: StickerPage[]): number {
  for (let i = pages.length - 1; i >= 0; i--) if (pages[i]!.earned > 0) return i;
  return 0;
}

/** The word a sticker stands for (衣服 for the sticker of 衣), from the lesson's bundle. */
export function stickerWord(bundle: LessonBundle, setId: string, char: string): Word | null {
  const entry = bundle.lesson.sets
    .find((x) => x.id === setId)
    ?.entries.find((e) => e.char === char);
  return entry?.wordId ? (bundle.words[entry.wordId] ?? null) : null;
}
